import { z } from "zod";

/**
 * Ce qu'il faut pour dessiner une carte du TCG (lot 5, plan `docs/plans/2026-10-31-castellan-tcg-cartes.md`).
 *
 * Ce fichier n'importe que `zod` : l'image du service de rendu le copie seul (comme `affiche.ts`), et rien
 * d'autre ne doit y entrer, sans quoi l'image ne se construirait plus. Aucune donnee d'une personne n'y vit :
 * seulement la forme de ce qu'on dessine.
 */

/** Les six raretes de l'echelle des rangs (`RANGS`, `viewers.ts`), puis le Chatelain, hors echelle. */
export const RARETES = [
  "ecuyer",
  "chevalier",
  "paladin",
  "heros",
  "seigneur",
  "legende",
  "chatelain",
] as const;
export type Rarete = (typeof RARETES)[number];

/** Les genres d'edition. `moment` est accepte par la base et par le composant ; sa creation attend le ticket 9. */
export const TYPES_EDITION = ["normale", "raid", "moment", "chatelain"] as const;
export type TypeEdition = (typeof TYPES_EDITION)[number];

export const carteImpressionSchema = z.strictObject({
  rarete: z.enum(RARETES),
  type: z.enum(TYPES_EDITION),
  nom: z.string().min(1).max(25),
  /** data URL (rendu, apercu d'une edition imprimee) ou adresse du meme site ; `null` : le monogramme. */
  avatar: z.string().min(1).max(2_000_000).nullable(),
  monogramme: z.string().length(1),
  /** `null` : « Chronique en cours d'ecriture » (carte de raid pas encore validee). */
  chronique: z.string().max(140).nullable(),
  mentionIa: z.boolean(),
  stats: z
    .array(
      z.strictObject({
        libelle: z.string().max(14),
        valeur: z.number().int().min(0).max(999_999),
      }),
    )
    .length(3),
  bandeau: z.string().max(40).nullable(),
  pied: z.strictObject({
    /** « Paladin », « Chatelain ». */
    rang: z.string().max(12),
    /** « 3e éd. », « hors série ». */
    edition: z.string().max(10),
    saison: z.number().int().positive(),
    numeroAlbum: z.number().int().min(0).max(9999),
  }),
});
export type CarteImpression = z.infer<typeof carteImpressionSchema>;

/** Le facteur d'impression (ecart 15 du plan) : les cartes sont imprimees au double de la taille des maquettes. */
export const ECHELLE_IMPRESSION = 2;

/** Les dimensions de chaque rarete, en pixels de maquette (celles des cadres de `docs/maquettes/castellan/assets`). */
export const DIMENSIONS_CARTE: Readonly<Record<Rarete, { largeur: number; hauteur: number }>> = {
  ecuyer: { largeur: 507, hauteur: 767 },
  chevalier: { largeur: 508, hauteur: 767 },
  paladin: { largeur: 508, hauteur: 768 },
  heros: { largeur: 520, hauteur: 700 },
  seigneur: { largeur: 520, hauteur: 732 },
  legende: { largeur: 520, hauteur: 748 },
  chatelain: { largeur: 520, hauteur: 736 },
};

/**
 * Le monogramme d'une carte sans avatar : la premiere lettre ou le premier chiffre du nom, en majuscule. Une seule
 * unite de texte, comme l'exige le contrat (`monogramme` fait 1 caractere) : un nom sans lettre, ou dont l'initiale
 * tiendrait sur deux unites (un caractere hors du plan de base), donne « ? ».
 */
export function monogramme(nom: string): string {
  const initiale = [...nom].find((caractere) => /^[\p{L}\p{N}]$/u.test(caractere));
  if (initiale === undefined) {
    return "?";
  }
  const majuscule = initiale.toLocaleUpperCase("fr");
  if (majuscule.length === 1) {
    return majuscule;
  }
  return initiale.length === 1 ? initiale : "?";
}
